import React from 'react';

import LinkIcon from '@atlaskit/icon/core/link';
import type { JsonLd } from '@atlaskit/json-ld-types/jsonld';
import { extractSmartLinkInlineIcon } from '@atlaskit/link-extractors/extract-smart-link-inline-icon';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import type { EnhancedSmartLink, SmartLinkIcon } from './smartLinkTypes.ts';

type SmartLinkIconSource = Pick<EnhancedSmartLink, 'ari' | 'occurrenceIndex' | 'product' | 'url'>;

const getUrlOrigin = (url: string): string => {
	try {
		return new URL(url).origin;
	} catch {
		return url;
	}
};

type SmartLinkInlineIconResponse = Parameters<typeof extractSmartLinkInlineIcon>[0];

const isUrlLabelTuple = (icon: unknown): icon is [string, string?] =>
	Array.isArray(icon) && typeof icon[0] === 'string';

const normalizeInlineIcon = (
	source: SmartLinkIconSource,
	inlineIcon: React.ReactNode,
	label?: string,
): SmartLinkIcon | undefined => {
	if (typeof inlineIcon === 'string') {
		return {
			src: inlineIcon,
			key: `url:${getUrlOrigin(source.url)}:${inlineIcon}`,
			label,
			kind: 'image',
		};
	}

	// Link extractors returns [url, label] for entity icons behind platform_lp_use_entity_icon_url_for_icon.
	if (isUrlLabelTuple(inlineIcon)) {
		const [iconUrl, iconLabel] = inlineIcon;
		return {
			src: iconUrl,
			key: `url:${getUrlOrigin(source.url)}:${iconUrl}`,
			label: iconLabel ?? label,
			kind: 'image',
		};
	}

	if (React.isValidElement(inlineIcon)) {
		return {
			node: inlineIcon,
			key: `node:${source.product ?? getUrlOrigin(source.url)}:${source.ari ?? source.occurrenceIndex}`,
			label: label ?? source.product,
			kind: 'element',
		};
	}

	return undefined;
};

// extractSmartLinkInlineIcon throws on malformed JSON-LD (e.g. a string `generator`, or an entity
// `meta.generator` missing name/icon). Fall back to a neutral link icon so one bad response doesn't
// break icon building for the whole byline — mirrors the failed-image fallback in SmartLinkIconRenderer.
const createFallbackIcon = (source: SmartLinkIconSource): SmartLinkIcon => ({
	node: React.createElement(LinkIcon, { label: '', color: token('color.icon.subtle') }),
	key: `fallback:${getUrlOrigin(source.url)}:${source.ari ?? source.occurrenceIndex}`,
	label: source.product,
	kind: 'element',
});

// Builds an image icon directly from a URL, for providers whose resolved payload lacks a top-level
// JSON-LD icon (e.g. shallow Jira REST issues, whose icon lives under data.fields.issuetype.iconUrl).
export const buildImageIcon = (url: string, source: SmartLinkIconSource): SmartLinkIcon =>
	normalizeInlineIcon(source, url, source.product) as SmartLinkIcon;

export const extractSmartLinkIcon = (
	response: JsonLd.Response | null | undefined,
	source: SmartLinkIconSource,
): SmartLinkIcon | undefined => {
	let inlineIcon: React.ReactNode;
	try {
		inlineIcon = extractSmartLinkInlineIcon(
			(response ?? undefined) as SmartLinkInlineIconResponse,
			false,
		);
	} catch (err: unknown) {
		const errorObj = err instanceof Error ? err : new Error(String(err));
		getMonitoringClient().submitError(errorObj, {
			attribution: Attribution.INTEGRATIONS,
		});
		return createFallbackIcon(source);
	}

	return normalizeInlineIcon(source, inlineIcon, source.product);
};

export const getSmartLinkIcon = (
	link: Pick<EnhancedSmartLink, 'ari' | 'icon' | 'iconUrl' | 'occurrenceIndex' | 'product' | 'url'>,
): SmartLinkIcon | undefined => {
	// Keep iconUrl as a compatibility fallback for EnhancedSmartLink objects built before icon normalization.
	return (
		link.icon ?? (link.iconUrl ? normalizeInlineIcon(link, link.iconUrl, link.product) : undefined)
	);
};
